'use client';

/**
 * A heading whose words rise into place from behind their own line, once,
 * as it first comes into view.
 *
 * After React Bits' SplitText (MIT + Commons Clause, used inside the product
 * as its licence allows), without its GSAP runtime: each word sits in a mask
 * one line tall and travels up out of it, a stagger step after the word
 * before, on the house spring. Crisp rather than soft — the word is either
 * hidden by its own line or fully drawn; nothing is ever blurred or faint
 * while it is being read.
 *
 * The server renders the heading whole. On mount it is only ARMED (words
 * dropped behind their masks) if it is still below the fold, so a heading
 * already on screen never flickers; reduced motion never arms it. State lives
 * in a data attribute, not React state — no re-render to reveal.
 */

import { createElement, useEffect, useRef, type HTMLAttributes } from 'react';
import { STAGGER_MS } from '@roomtreatment/design';

type Props = {
  text: string;
  as?: 'h2' | 'h3' | 'p';
} & Omit<HTMLAttributes<HTMLElement>, 'children'>;

export default function RevealHeading({ text, as = 'h2', ...rest }: Props) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.dataset.reveal = 'armed';
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        el.dataset.reveal = 'shown';
        io.disconnect();
      },
      { rootMargin: '0px 0px -12% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [text]);

  const words = text.split(' ');
  return createElement(
    as,
    { ...rest, ref },
    words.map((word, i) => (
      <span key={i}>
        <span className="reveal-mask">
          <span data-word className="reveal-word" style={{ transitionDelay: `${i * STAGGER_MS}ms` }}>{word}</span>
        </span>
        {i < words.length - 1 ? ' ' : null}
      </span>
    )),
  );
}
